import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Colors } from 'modules/theme';
import Loader from 'components/Loader';
import H5 from 'components/H5';
import {
  FLOW_VOLUME_BAR_RADIUS,
  computeBarSize,
  formatFlowVolumeTickFromPoint,
  formatFlowVolumeXAxisLabel,
  getFlowVolumeChartMargin,
  getXAxisInterval,
} from 'utils/flowVolumeChart';
import {
  CHART_TOOLTIP_CURSOR_BAR,
  CHART_TOOLTIP_WRAPPER_STYLE,
  FlowVolumeTooltip,
} from './chartTooltip';
import { ChartPanel } from './flowVolume.styles';

const CHART_AXIS = '#94a3b8';
const CHART_GRID = '#e3edf5';
const BAR_GRADIENT_ID = 'flowVolumeBarGradient';

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(248, 251, 255, 0.82);
  z-index: 10;
  border-radius: 12px;
`;

const LoadingCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #fff;
  padding: 16px 20px;
  border-radius: 10px;
  border: 1px solid #e8f1f8;
  box-shadow: 0 4px 16px rgba(20, 152, 203, 0.1);
`;

const FlowVolumeChart = ({
  id,
  dataChart,
  consumption,
  loading,
  pressureLoading,
  showPressure = true,
}) => {
  const panelRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(600);
  const pointCount = dataChart.length;
  const barSize = computeBarSize(pointCount, chartWidth);
  const xAxisInterval = getXAxisInterval(pointCount, chartWidth, 48);
  const chartMargin = getFlowVolumeChartMargin(pointCount);
  const denseAxis = pointCount > 36;
  const showPressureLines = showPressure && !loading && !pressureLoading;

  const renderXAxisTick = ({ x, y, payload }) => {
    const point = dataChart[payload?.index];
    const text =
      formatFlowVolumeTickFromPoint(point, pointCount) ||
      formatFlowVolumeXAxisLabel(payload.value, payload?.index ?? 0, dataChart);
    const lines = String(text).split('\n');

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={12}
          textAnchor="middle"
          fill={CHART_AXIS}
          fontSize={denseAxis ? 10 : 11}
        >
          {lines.map((line, lineIndex) => (
            <tspan key={lineIndex} x={0} dy={lineIndex === 0 ? 0 : 12}>
              {line}
            </tspan>
          ))}
        </text>
      </g>
    );
  };

  useEffect(() => {
    const node = panelRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect?.width;
      if (width) {
        setChartWidth(width);
      }
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <ChartPanel id={id} ref={panelRef}>
      {loading && (
        <LoadingOverlay>
          <LoadingCard>
            <Loader size={30} />
            <H5 color={Colors.gray8} style={{ marginTop: 10, margin: 0 }}>
              Loading chart data...
            </H5>
          </LoadingCard>
        </LoadingOverlay>
      )}
      <ResponsiveContainer width="100%" height="100%" debounce={50}>
        <ComposedChart data={loading ? [] : dataChart} barCategoryGap="10%" margin={chartMargin}>
          <defs>
            <linearGradient id={BAR_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3ec5f2" />
              <stop offset="100%" stopColor={Colors.lightBlue} />
            </linearGradient>
          </defs>

          <XAxis
            type="category"
            dataKey="name"
            scale="point"
            axisLine={false}
            tickLine={false}
            stroke={CHART_AXIS}
            interval={xAxisInterval}
            tick={renderXAxisTick}
            tickMargin={denseAxis ? 6 : 10}
            padding={{ left: 16, right: 16 }}
            minTickGap={8}
          />

          <YAxis
            yAxisId="flow"
            type="number"
            unit=" gal"
            width={54}
            axisLine={false}
            tickLine={false}
            stroke={CHART_AXIS}
            tick={{ fontSize: 11, fill: CHART_AXIS }}
          />
          <YAxis yAxisId="pressure" type="number" orientation="right" hide />

          <Tooltip
            content={<FlowVolumeTooltip />}
            cursor={CHART_TOOLTIP_CURSOR_BAR}
            wrapperStyle={CHART_TOOLTIP_WRAPPER_STYLE}
          />

          <CartesianGrid stroke={CHART_GRID} strokeDasharray="4 6" vertical={false} />

          <Bar
            yAxisId="flow"
            dataKey="gal"
            legendType="none"
            fill={`url(#${BAR_GRADIENT_ID})`}
            radius={FLOW_VOLUME_BAR_RADIUS}
            barSize={barSize}
            isAnimationActive={!loading}
            animationDuration={600}
          >
            {(loading ? [] : consumption).map((entry, index) => (
              <Cell key={`flow-cell-${index}`} fill={`url(#${BAR_GRADIENT_ID})`} />
            ))}
          </Bar>

          {showPressureLines && (
            <>
              <Line
                yAxisId="pressure"
                dataKey="psi_0"
                type="monotone"
                stroke="#2563eb"
                strokeWidth={2}
                dot={false}
                strokeOpacity={0.9}
                animationDuration={800}
              />
              <Line
                yAxisId="pressure"
                dataKey="psi_1"
                type="monotone"
                stroke="#22c55e"
                strokeWidth={2}
                dot={false}
                strokeOpacity={0.9}
                animationDuration={900}
              />
              <Line
                yAxisId="pressure"
                dataKey="psi_2"
                type="monotone"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
                strokeOpacity={0.9}
                animationDuration={1000}
              />
            </>
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartPanel>
  );
};

FlowVolumeChart.propTypes = {
  consumption: PropTypes.array,
  dataChart: PropTypes.array,
  id: PropTypes.string,
  loading: PropTypes.bool,
  pressureLoading: PropTypes.bool,
  showPressure: PropTypes.bool,
};

FlowVolumeChart.defaultProps = {
  consumption: [],
  dataChart: [],
  id: 'totalGaloonsChart',
  loading: false,
  pressureLoading: false,
  showPressure: true,
};

export default FlowVolumeChart;
